import React, { useCallback, useState, useEffect } from "react";

import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  MenuItem,
  Select,
  TextField, 
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  FormControl,
  InputLabel,
  useTheme,
  Chip,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import { BarChart, LineChart } from '@mui/x-charts';

import { oauthUserMgmt, headers , tenant, userLogOut} from "../utils/utils";
import HomeIcon from "@mui/icons-material/Home";
import { useNavigate } from "react-router-dom";
import { IconButton } from "@mui/material";
import { PieChart } from "@mui/x-charts";
import DashboardGantt from "./DashboardGantt";
import PersonIcon from "@mui/icons-material/Person";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";


const getStatusColor = (status) => {
  const s = (status || "").toLowerCase();
  if (s.includes("done") || s.includes("complete")) return "success";
  if (s.includes("progress")) return "warning";
  if (s.includes("review")) return "info";
  if (s.includes("bug")) return "error";
  return "default"; // or 'primary'
};

const ModernCard = ({ children, sx, ...props }) => (
  <Card
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      borderRadius: 4, // More rounded (approx 24px)
      boxShadow: "0px 10px 30px rgba(0,0,0, 0.04)", // Soft diffuse shadow
      border: "1px solid rgba(255,255,255, 0.6)",
      bgcolor: "#ffffff",
      transition: "transform 0.2s ease-in-out",
      "&:hover": {
        transform: "translateY(-4px)",
        boxShadow: "0px 15px 35px rgba(0,0,0, 0.08)",
      },
      ...sx,
    }}
    {...props}
  >
    {children}
  </Card>
);

// A reusable header for charts
const ChartHeader = ({ title, subtitle }) => (
  <Box sx={{ mb: 2, px: 1 }}>
    <Typography variant="h6" fontWeight={700} color="#1a202c">
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

// Common Table Header Style
const tableHeadStyle = {
  backgroundColor: "#f8f9fa",
  "& th": {
    color: "#6b778c",
    fontWeight: 600,
    fontSize: "0.75rem",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #eaeff5",
  },
};

// Custom Color Palette for Charts (Trending tech colors)
const chartPalette = ["#6366f1", "#8b5cf6", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444"];


// =====================================================
// SUB DASHBOARD (Material UI + MUI X Charts)
// =====================================================

export default function SubDashboard() {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatDateLocal = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const getDefaultDates = () => {
    const today = new Date();

    const firstDayOfMonth = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    return {
      startDate: formatDateLocal(firstDayOfMonth), // ✅ 01-12-2025
      endDate: formatDateLocal(today),              // ✅ today
    };
  };


  const defaultDates = getDefaultDates();
  const [filters, setFilters] = React.useState({
    startDate: defaultDates.startDate,
    endDate: defaultDates.endDate,
  });

  const [board, setBoard] = useState(() => localStorage.getItem("board"));
  const [workflowData, setWorkflowData] = useState([]);
const [loadingWorkflow, setLoadingWorkflow] = useState(false);
const [workflowHours, setWorkflowHours] = useState([]);
const [loadingHours, setLoadingHours] = useState(false);
const [userHours, setUserHours] = useState([]); // MUST be []
const [tagHours, setTagHours] = useState([]);
const [loadingTagHours, setLoadingTagHours] = useState(false);

const [memberList, setMemberList] = useState([]);
const [selectedUser, setSelectedUser] = useState("");
const [userCardHours, setUserCardHours] = useState([]);
const [loadingUserCards, setLoadingUserCards] = useState(false);  

const [tagList, setTagList] = useState([]);
const [selectedTag, setSelectedTag] = useState("");
const [tagCardHours, setTagCardHours] = useState([]);
const [loadingTagCards, setLoadingTagCards] = useState(false);



useEffect(() => {
    const handleBoardUpdate = () => {
      const newBoard = localStorage.getItem("board");
      setBoard((prev) => (prev !== newBoard ? newBoard : prev));
    };
  
    window.addEventListener("board-changed", handleBoardUpdate);
    window.addEventListener("storage", handleBoardUpdate);
  
    return () => {
      window.removeEventListener("board-changed", handleBoardUpdate);
      window.removeEventListener("storage", handleBoardUpdate);
    };
  }, []);
  
  useEffect(() => {
    fetchAllMember();
    fetchLabel();
  }, []);
  

  const fetchAllMember = async () => {
    if (memberList.length === 0)
      try {
        const response = await fetch(
          process.env.REACT_APP_USER_MGMT +
          "/admin/realms/eVyoog/users?q=tenant:" +
          tenant(),
          {
            method: "GET",
            headers: oauthUserMgmt(),
          }
        );
        if (response.ok) {
          const result = await response.json();

          const usersWithFullNameAndId = result
            ? result
              .filter((item) => item.firstName)
              .map((user) => ({
                id: user.id,
                fullName: `${user.firstName ? user.firstName : ""} ${user.lastName}`,
              }))
            : null;

          setMemberList(usersWithFullNameAndId);
        }
      } catch (error) {
        userLogOut();
      }
  };

  const fetchLabel = async () => {
    if (tagList.length === 0) {
      try {
        const response = await fetch(
          process.env.REACT_APP_SYS_CONFIG_MGMT +
          "/systemConfig/config?name=TicketLabel-" +
          localStorage.getItem("board"),
          {
            method: "GET",
            headers: headers(),
          }
        );

        const result = await response.json();
        setTagList(result);
      } catch (error) {
        console.error("Error fetching labels:", error);
      }
    }
  };


  const loadWorkflowTaskCount = useCallback(async () => {
    if (!board) return;
  
    try {
      setLoadingWorkflow(true);
  
      const params = new URLSearchParams({
        board,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
  
      const response = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/workflow-task-count?${params}`,
        {
          method: "GET",
          headers: headers(),
        }
      );
  
      if (!response.ok) throw new Error("Failed to load workflow count");
  
      const data = await response.json();
      setWorkflowData(data);
  
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingWorkflow(false);
    }
  }, [board, filters.startDate, filters.endDate]);
  
  const totalTasks = workflowData.reduce(
    (sum, w) => sum + w.count,
    0
  );
  
  const pieData = workflowData.map((w, index) => ({
    id: index,
    label: w.workflow,
    value: w.count,          // PieChart needs raw value
    percentage: totalTasks
      ? ((w.count / totalTasks) * 100).toFixed(1)
      : 0,
  }));
  

  const loadWorkflowHours = useCallback(async () => {
    if (!board) return;
  
    try {
      setLoadingHours(true);
      const params = new URLSearchParams({
        board,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });

      
      const res = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/workflow-hours?${params}`,
        {  method: "GET",headers: headers() }
      );
      setWorkflowHours(await res.json());
    } finally {
      setLoadingHours(false);
    }
  }, [board]);

  const filteredWorkflowHours = React.useMemo(() => {
    return workflowHours
      .filter(row => row.workflow && row.workflow.trim() !== "") // ❌ remove empty
      .map(row => ({
        ...row,
        workflow: row.workflow.trim(), // optional cleanup
      }));
  }, [workflowHours]);
  

  const loadUserHours = useCallback(async () => {
    if (!board) return;
  
    const params = new URLSearchParams({
      board,
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
  
    const res = await fetch(
      `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/user-work-hours?${params}`,
      { headers: headers() }
    );
  
    if (!res.ok) throw new Error("Failed to load user hours");
  
    setUserHours(await res.json());
  }, [board, filters]);


  const userNameMap = React.useMemo(() => {
    const map = {};
    memberList.forEach(m => {
      map[m.id] = m.fullName?.trim() || m.username;
    });
    return map;
  }, [memberList]);
  
  
  const userBarData = React.useMemo(() => {
    return [...userHours]
      .map(row => ({
        ...row,
        userName: userNameMap[row.userId] || `User ${row.userId}`,
      }))
      .sort((a, b) => b.totalHours - a.totalHours);
  }, [userHours, userNameMap]);
  


  const loadTagHours = useCallback(async () => {
    if (!board) return;
  
    try {
      setLoadingTagHours(true);
  
      const params = new URLSearchParams({
        board,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
  
      const res = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/tag-hours?${params}`,
        { headers: headers() }
      );
  
      if (!res.ok) throw new Error("Failed to load tag hours");
  
      const data = await res.json();
  
      // 🔥 IMPORTANT: Ensure array
      setTagHours(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setTagHours([]);
    } finally {
      setLoadingTagHours(false);
    }
  }, [board, filters.startDate, filters.endDate]);
  
  const tagNameMap = React.useMemo(() => {
    const map = {};
    tagList.forEach((t) => {
      map[t.id] = t.value;   // value = tag name
    });
    return map;
  }, [tagList]);

  const tagPieData = React.useMemo(() => {
    return tagHours.map((row, index) => ({
      id: index,
      label: tagNameMap[row.tagId] || `Tag ${row.tagId}`, // ✅ name instead of id
      value: row.totalHours,
    }));
  }, [tagHours, tagNameMap]);
  
  

  const loadUserCardHours = useCallback(async () => {
    if (!board || !selectedUser) return;
  
    try {
      setLoadingUserCards(true);
  
      const params = new URLSearchParams({
        board,
        userId: selectedUser,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
  
      const res = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/user-card-hours?${params}`,
        { headers: headers() }
      );
  
      const data = await res.json();
      setUserCardHours(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setUserCardHours([]);
    } finally {
      setLoadingUserCards(false);
    }
  }, [board, selectedUser, filters]);

  const totalUserHours = userCardHours.reduce(
    (sum, row) => sum + (row.totalHours || 0),
    0
  );

  const loadTagCardHours = useCallback(async () => {
    if (!board || !selectedTag) return;
  
    try {
      setLoadingTagCards(true);
  
      const params = new URLSearchParams({
        board,
        tagId: selectedTag,
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
  
      const res = await fetch(
        `${process.env.REACT_APP_TICKET_SERVER}/api/dashboard/tag-card-hours?${params}`,
        { headers: headers() }
      );
  
      const data = await res.json();
      setTagCardHours(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      setTagCardHours([]);
    } finally {
      setLoadingTagCards(false);
    }
  }, [board, selectedTag, filters.startDate, filters.endDate]);
  
  const totalTagHours = tagCardHours.reduce(
    (sum, row) => sum + (row.totalHours || 0),
    0
  );
  
  // const loadAllDashboardData = React.useCallback(async () => {
  //   await Promise.all([
  //       loadWorkflowTaskCount(),
  //       loadWorkflowHours(),
  //       loadUserHours(),
  //       loadTagHours(),
  //       loadUserCardHours(),
  //       loadTagCardHours()
  //   ]);
  // }, [loadWorkflowTaskCount, loadWorkflowHours, loadUserHours, loadTagHours, loadUserCardHours, loadTagCardHours]);

  // React.useEffect(() => {
  //   if (board) {
  //       loadAllDashboardData();
  //     }
  // }, [board, filters, loadAllDashboardData]);

  useEffect(() => {
    if (!board) return;
  
    loadWorkflowTaskCount();
    loadWorkflowHours();
    loadUserHours();
    loadTagHours();
  
  }, [board, filters.startDate, filters.endDate]);
  
  useEffect(() => {
    if (!board || !selectedUser) return;
  
    loadUserCardHours();
  
  }, [board, selectedUser, filters.startDate, filters.endDate]);
  
  useEffect(() => {
    if (!board || !selectedTag) return;
  
    loadTagCardHours();
  
  }, [board, selectedTag, filters.startDate, filters.endDate]);
  

  return (
    <>
    <Box
      p={4}
      sx={{
        bgcolor: "#f0f2f5", // Very light gray/blue background
        minHeight: "100vh",
        background: "radial-gradient(circle at 10% 20%, rgb(243, 247, 255) 0%, rgb(240, 242, 245) 90%)",
      }}
    >
      {/* ================= Header & Filters ================= */}
      <Box mb={4} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems="center" gap={3}>
        
        {/* Page Title */}
        <Box>
            <Typography variant="h4" fontWeight={800} color="#1e293b" letterSpacing="-0.5px">
                Analytics Dashboard
            </Typography>
            <Typography variant="body1" color="text.secondary">
                Overview of workflow performance and time tracking
            </Typography>
        </Box>

        {/* Floating Filter Card */}
        <Box 
          sx={{ 
            p: 1.5, 
            bgcolor: "white", 
            borderRadius: 50, 
            boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 2,
            border: "1px solid #edf2f7"
          }}
        >
          <TextField
            type="date"
            variant="standard" // Cleaner look
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', fontWeight: 600, color: '#475569' } }}
            value={filters.startDate}
            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            sx={{ px: 2 , "& input": { p: 0, cursor: 'pointer' }}}
          />
          <Box sx={{ width: '1px', height: '20px', bgcolor: '#e2e8f0' }} /> {/* Divider */}
          <TextField
            type="date"
            variant="standard"
            InputProps={{ disableUnderline: true, sx: { fontSize: '0.9rem', fontWeight: 600, color: '#475569' } }}
            value={filters.endDate}
            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            sx={{ px: 2 , "& input": { p: 0, cursor: 'pointer' }}}
          />
          
          <IconButton
            onClick={() => navigate("/ticket")}
            sx={{
              bgcolor: "primary.main",
              color: "white",
              width: 40,
              height: 40,
              "&:hover": { bgcolor: "primary.dark" , transform: "scale(1.05)"},
            }}
          >
            <HomeIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* ================= CHARTS GRID ================= */}
      <Grid container spacing={3}>
        
        {/* --- PIE CHART: Workflow Task Distribution --- */}
        <Grid item xs={12} md={6} lg={4}>
          <ModernCard>
            <CardContent sx={{ p: 4 }}>
              <ChartHeader title="Task Distribution" subtitle="Breakdown by workflow type" />
              
              <Box height={300} display="flex" alignItems="center" justifyContent="center">
                {loadingWorkflow ? (
                  <CircularProgress color="secondary" />
                ) : pieData.length === 0 ? (
                  <Typography color="text.secondary">No Data</Typography>
                ) : (
                  <PieChart
                    colors={chartPalette}
                    series={[
                      {
                        data: pieData,
                        innerRadius: 80, // Thinner donut
                        outerRadius: 120,
                        paddingAngle: 5, // Spacing between slices
                        cornerRadius: 8, // Rounded slice edges
                        arcLabel: (item) => `${item.percentage}%`,
                        arcLabelMinAngle: 15,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                      },
                    ]}
                    slotProps={{
                        legend: { hidden: true }, // Hide default legend for cleaner look
                        tooltip: { trigger: "item" , formatter: (item) =>
                          `${item.label}: ${item.value} tasks`} 
                    }}
                    sx={{
                      "& .MuiPieArcLabel-root": {
                        fill: "#fff",        // 🔥 white text
                        fontWeight: 600,
                        fontSize: "12px",
                      },
                    }}
                    height={250}
                    margin={{ right: 5 }}
                  />

                )}
              </Box>
              {/* Custom Legend / Summary could go here */}
            </CardContent>
          </ModernCard>
        </Grid>

        {/* --- BAR CHART: Workflow Effort --- */}
        <Grid item xs={12} md={6} lg={8}>
          <ModernCard>
            <CardContent sx={{ p: 4 }}>
              <ChartHeader title="Workflow Effort" subtitle="Total hours spent per workflow" />
              
              <Box height={300}>
                <BarChart
                  dataset={filteredWorkflowHours}
                  colors={['#6366f1']} // Modern Indigo
                  xAxis={[{ 
                    scaleType: "band", 
                    dataKey: "workflow",
                    tickLabelStyle: { fontSize: 12, fill: '#64748b' }
                  }]}
                  yAxis={[{
                    labelStyle: { fill: '#64748b' }
                  }]}
                  series={[{ 
                    dataKey: "totalHours", 
                    valueFormatter: (v) => `${v}h`,
                    highlightScope: { faded: 'global', highlighted: 'item' },
                  }]}
                  borderRadius={8} // Rounded bar tops
                  grid={{ horizontal: true }} // Add grid lines for readability
                  sx={{
                    "& .MuiChartsGrid-line": { strokeDasharray: "4 4", stroke: "#e2e8f0" }
                  }}
                />
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        {/* --- BAR CHART: User Work Hours (Horizontal) --- */}
        <Grid item xs={12} md={6}>
          <ModernCard>
            <CardContent sx={{ p: 4 }}>
              <ChartHeader title="Team Performance" subtitle="Hours logged by user" />

              <Box height={320}>
                <BarChart
                  layout="horizontal"
                  dataset={userBarData}
                  colors={['#0ea5e9']} // Sky blue
                  yAxis={[{ 
                    scaleType: "band", 
                    dataKey: "userName",
                    tickLabelStyle: { fontSize: 13, fontWeight: 500, fill: '#334155' } 
                   }]}
                  series={[{ 
                    dataKey: "totalHours", 
                    valueFormatter: (v) => `${v.toFixed(1)}h`,
                    label: "Total Hours" 
                  }]}
                  borderRadius={6}
                  grid={{ vertical: true }}
                  sx={{
                    "& .MuiChartsGrid-line": { strokeDasharray: "4 4", stroke: "#e2e8f0" }
                  }}
                />
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>

        {/* --- PIE CHART: Tag-wise Work Hours --- */}
        <Grid item xs={12} md={6}>
          <ModernCard>
            <CardContent sx={{ p: 4 }}>
               <ChartHeader title="Tags Analysis" subtitle="Distribution of hours by tag" />

              <Box height={320} display="flex" alignItems="center" justifyContent="center">
                {loadingTagHours ? (
                  <CircularProgress />
                ) : tagPieData.length === 0 ? (
                  <Typography color="text.secondary">No Data</Typography>
                ) : (
                  <PieChart
                    colors={chartPalette}
                    series={[
                      {
                        data: tagPieData,
                        innerRadius: 0, // Full pie for variety
                        outerRadius: 130,
                        paddingAngle: 2,
                        cornerRadius: 4,
                        arcLabel: (item) => item.value > 5 ? `${item.value.toFixed(0)}h` : '', // Only show label if big enough
                        arcLabelMinAngle: 20,
                        highlightScope: { faded: 'global', highlighted: 'item' },
                      },
                    ]}
                    slotProps={{
                      legend: {
                        direction: 'column',
                        position: { vertical: 'middle', horizontal: 'right' },
                        itemMarkWidth: 10,
                        itemMarkHeight: 10,
                        labelStyle: { fontSize: 12, fill: '#475569' }
                      },
                    }}
                  />
                )}
              </Box>
            </CardContent>
          </ModernCard>
        </Grid>
      </Grid>
      <Box sx={{ my: 4 }} />
      <Grid container spacing={3} alignItems="stretch" mb={3}>
      {/* ================= USER WORK BREAKDOWN ================= */}
      <Grid item xs={12} md={6}>
      <ModernCard sx={{ height: 500, display: "flex", flexDirection: "column" }}>
          {/* Header Section */}
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: theme.palette.primary.light,
                  color: theme.palette.primary.main,
                }}
              >
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                  User Breakdown
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Track hours by team member
                </Typography>
              </Box>
            </Stack>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUser}
                label="Select User"
                onChange={(e) => setSelectedUser(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {memberList.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.fullName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>


          <CardContent sx={{ p: 0,flexGrow: 1, display: 'flex', flexDirection: 'column' , overflow: "hidden"}}>
            {loadingUserCards ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={30} thickness={4} />
              </Box>
            ) : userCardHours.length === 0 ? (
              <Box
                py={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
                color="text.secondary"
              >
                <WorkOutlineIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography>No data available for this user</Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" , flexGrow: 1 }}>
                <Table stickyHeader size="medium">
                  <TableHead sx={tableHeadStyle}>
                    <TableRow>
                      <TableCell sx={{ pl: 3 }}>Card ID</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Workflow</TableCell>
                      <TableCell align="right" sx={{ pr: 3 }}>Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userCardHours.map((row, i) => (
                      <TableRow
                        key={i}
                        hover
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            sx={{
                              bgcolor: "#f1f5f9",
                              color: "#475569",
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              display: "inline-block",
                              fontWeight: 600,
                             
                            }}
                          >
                            {row.ticketCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                            noWrap
                            maxWidth={200}
                          >
                            {row.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                       
                          <Chip
                            label={row.workflow}
                            size="small"
                            color={getStatusColor(row.workflow)}
                            variant="outlined" // or "filled" for bolder look
                            sx={{ borderColor: 'divider', fontSize: "0.7rem", height: 24,
                            border: '1px solid' }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {row.totalHours.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>

          {/* Footer Total */}
          {userCardHours.length > 0 && (
            <Box
              sx={{
                bgcolor: theme.palette.primary.main,
                color: "#fff",
                p: 2,
                mt: "auto", // Push to bottom if height is fixed
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Total User Contribution
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="h6" fontWeight={700}>
                  {totalUserHours.toFixed(2)} hrs
                </Typography>
              </Stack>
            </Box>
          )}
        </ModernCard>
      </Grid>

      {/* ================= TAG WORK BREAKDOWN ================= */}
      <Grid item xs={12} md={6}>
      <ModernCard sx={{ height: 500, display: "flex", flexDirection: "column" }}>
          {/* Header Section */}
          <Box
            sx={{
              p: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar
                sx={{
                  bgcolor: theme.palette.secondary.light,
                  color: theme.palette.secondary.main,
                }}
              >
                <LocalOfferIcon />
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700} color="text.primary">
                  Tag Analysis
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Hours distributed by tag
                </Typography>
              </Box>
            </Stack>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Select Tag</InputLabel>
              <Select
                value={selectedTag}
                label="Select Tag"
                onChange={(e) => setSelectedTag(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {tagList.map((tag) => (
                  <MenuItem key={tag.id} value={tag.id}>
                    {tag.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>


          <CardContent sx={{ p: 0,flexGrow: 1, display: 'flex', flexDirection: 'column' , overflow: "hidden"}}>
            {loadingTagCards ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress size={30} thickness={4} color="secondary" />
              </Box>
            ) : tagCardHours.length === 0 ? (
              <Box
                py={6}
                display="flex"
                flexDirection="column"
                alignItems="center"
                color="text.secondary"
              >
                <LocalOfferIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography>No data available for this tag</Typography>
              </Box>
            ) : (
              <Box sx={{ overflowX: "auto" , flexGrow: 1 }}>
                <Table stickyHeader size="medium">
                  <TableHead sx={tableHeadStyle}>
                    <TableRow>
                      <TableCell>Card ID</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Workflow</TableCell>
                      <TableCell align="right">Hours</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tagCardHours.map((row, i) => (
                      <TableRow
                        key={i}
                        hover
                        sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      >
                        <TableCell>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            sx={{
                              bgcolor: "#f3f6f9",
                              p: 0.5,
                              borderRadius: 1,
                              display: "inline-block",
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            {row.ticketCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500 }}
                            noWrap
                            maxWidth={200}
                          >
                            {row.subject}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={row.workflow}
                            size="small"
                            variant="outlined"
                            sx={{
                                borderColor: 'divider',
                                color: 'text.secondary',
                                fontSize: "0.7rem"
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight={600}>
                            {row.totalHours.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>

          {/* Footer Total */}
          {tagCardHours.length > 0 && (
            <Box
              sx={{
                bgcolor: theme.palette.secondary.main,
                color: "#fff",
                p: 2,
                mt: "auto",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                borderBottomLeftRadius: 16,
                borderBottomRightRadius: 16,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                Total Tag Hours
              </Typography>
              <Stack direction="row" alignItems="center" spacing={1}>
                <AccessTimeIcon fontSize="small" />
                <Typography variant="h6" fontWeight={700}>
                  {totalTagHours.toFixed(2)} hrs
                </Typography>
              </Stack>
            </Box>
          )}
        </ModernCard>
      </Grid>
    </Grid>

   </Box>

  

  <Card sx={{ mb: 3 }}>
  <CardContent>
    <DashboardGantt
      board={board}
      startDate={filters.startDate}
      endDate={filters.endDate}
    />
  </CardContent>
</Card>

    
    </>
  );
}
